import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { backupsApi } from 'src/api_services/backups/service';

import { Box } from 'src/components/Box';

import { H1, Text } from 'src/components/Text';

import { GlobalWebsiteBackup, BackupStorageKind, BackupKind, OperationStatus } from 'src/orchd-client';
import { AppState } from 'src/store';
import { sessionSelectors } from 'src/store/session/selectors';
import { VaultInfo, WebsiteBackupCard } from '../websites/website/backups/WebsiteBackupCard';
import { website } from 'src/mocks/websites/websites';
import { getErrorInfo } from 'src/utils/errors';
import { toastActions } from 'src/store/toast';
import { RestoreProgress } from './RestoreProgress';
import { LoadingCard } from 'src/components/LoadingCard';
import { usePagination } from 'src/hooks/usePagination/usePagination';
import sortBy from 'lodash/sortBy';
import { Pagination } from 'src/components/Pagination/Pagination';
import { ResourceCount } from 'src/components/ResourceCount/ResourceCount';
import Search from 'src/components/Search';
import { useToast } from 'src/utils/toast/useToast';

import { EmptyState } from 'src/components/EmptyState/EmptyState';


type StateProps = ReturnType<typeof mapStateToProps>;
type ActionProps = typeof dispatchToProps;

export interface Props extends StateProps, ActionProps {

};

interface GlobalWebsiteBackupWithServerId extends GlobalWebsiteBackup {
  serverId: string
}

export const dispatchToProps = {
  errorToast: toastActions.errorToast,
};


export type WebsiteRestoring = {
  id: string;
  domain: string;
  date: string;
}

export type Status = {
  percentage: number;
  lastLog: string,
  isComplete: boolean
}

export const _VaultOverview = ({ member, errorToast }: Props) => {
  const [backups, setBackups] = useState<GlobalWebsiteBackupWithServerId[]>([]);
  const [websitesRestoring, setWebsitesRestoring] = useState<WebsiteRestoring[]>([]);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const toast = useToast();
  const [loading, setLoading] = useState(true);

  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const intervalsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const backupsNotRestored = backups.filter(backup => !statuses[backup.websiteId]?.isComplete).filter(backup =>
    searchTerm ? backup.primaryDomain.includes(searchTerm) : backup
  );
  const { paginated, itemsPerPage, page, onPageChange } = usePagination(sortBy(backupsNotRestored, ['primaryDomain']));



  useEffect(() => {
    // Clear intervals for websites no longer in the list
    Object.keys(intervalsRef.current).forEach((id) => {
      if (!websitesRestoring.find(restoring => restoring.id === id)) {
        clearInterval(intervalsRef.current[id]);
        delete intervalsRef.current[id];
      }
    });

    websitesRestoring.forEach((restoring) => {
      if (!intervalsRef.current[restoring.id]) {
        const poll = async () => {
          try {
            const res = await backupsApi.getWebsiteRestoreLog({
              websiteId: restoring.id
            });

            if(res.data.length < 1) {
              return;
            }

            const data: Status = {
              lastLog: res.data.pop(),
              percentage: 20,
              isComplete: false
            };

            setStatuses((prev) => ({ ...prev, [restoring.id]: data }));

          } catch (e) {
            console.error(`Error polling ${restoring.id}:`, e);
            clearInterval(intervalsRef.current[restoring.id]);
            delete intervalsRef.current[restoring.id];
            const data = {
              lastLog: "",
              percentage: 100,
              isComplete: true
            }
            setStatuses((prev) => ({ ...prev, [restoring.id]: data }));
          }
        };

        poll(); // initial call
        intervalsRef.current[restoring.id] = setInterval(poll, 500);
      }
    });

    return () => {
      // Clean up all intervals on component unmount
      Object.values(intervalsRef.current).forEach(clearInterval);
      intervalsRef.current = {};
    };
  }, [websitesRestoring]);

  const restoreVaultBackup = async (vaultInfo: VaultInfo) => {
    try {
      await backupsApi.restoreHardDeletedBackup({
        websiteId: vaultInfo.websiteId,
        serverId: vaultInfo.serverId,
      });

        setWebsitesRestoring(prev => [{
          id: vaultInfo.websiteId,
          domain: vaultInfo.domain,
          date: vaultInfo.createdAt
        }, ...prev, ]);
    } catch(e) {
      const error = getErrorInfo(e);
      console.log(error);

      errorToast(
        <FormattedMessage
          id="backups.vault.errors.failed_to_restore"
          defaultMessage="Failed to restore backup: {code} {error}"
          values={{ error: error.statusText, code: error.status }}
        />
      );
    }
  }

  const handleDeleteBackups = async (vaultInfo: VaultInfo) => {
    toast.create({
      id: vaultInfo.websiteId,
      variant: "neutral",
      message: `Deleting backup for ${vaultInfo.domain}`,
      persist: true
    });

    try {
      await backupsApi.deleteAllWebsiteBackups({ websiteId: vaultInfo.websiteId, serverId: vaultInfo.serverId });
      toast.dismiss(vaultInfo.websiteId);
      toast.success(`${vaultInfo.domain} backups deleted`);
      setBackups(backups.filter(backup => backup.websiteId !== vaultInfo.websiteId))
    } catch(error) {
      toast.dismiss(vaultInfo.websiteId);
      toast.error(`${vaultInfo.domain} backups failed to delete.  If this was a large website, you may want to try again.`)
    }
  }


  useEffect(() => {
    backupsApi.listGlobalBackups({})
      .then((res) => {
        return res.data;
      })
      .then((data) => {
        const flattened = Object.entries(data).flatMap(([key, snapshots]) =>
          snapshots.map(snapshot => ({ ...snapshot, serverId: key }))
        )
        setBackups(flattened);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <LoadingCard />;
  }

  const description = <FormattedMessage id="backups.vault.long_description" defaultMessage="Browse, restore or delete backups of permanently deleted websites from here.  This feature requires the {enhance} backup role to be installed on at least one of your servers and the backup role for your websites to have been placed there.  Only websites which have been permanently deleted will have their backups displayed." values={{ enhance: "Enhance" }} />
  const descriptionShort = <FormattedMessage id="backups.vault.short_description" defaultMessage="Browse, restore or delete backups of permanently deleted websites from here. " />

  const restoreProgress = websitesRestoring.length > 0 && (<RestoreProgress statuses={statuses} websitesRestoring={websitesRestoring} setWebsitesRestoring={setWebsitesRestoring} />);

  if(backupsNotRestored.length < 1 && !searchTerm) {
    return (
      <Fragment>
        {restoreProgress}
        <Box mb="2xl" a="center" j="space-between">
          <H1>
            <FormattedMessage defaultMessage="Archived backups" id="backups.vault.archived_backups_name" />
          </H1>
        </Box>
        <EmptyState title={<FormattedMessage id="backups.vault.empty_state_title" defaultMessage="No archive backups yet" />} message={description} icon="vault empty-state" />
      </Fragment>
    )
  }

  return (
    <Box>
      {restoreProgress}
      <Box mb="xs" a="center" j="space-between">
        <H1>
          <FormattedMessage defaultMessage="Archived backups" id="backups.vault.archived_backups_name" />
        </H1>


      </Box>

      <Box mb="2xl">
        <Text size="sm">{descriptionShort}</Text>
      </Box>




      <Box w={12} j="flex-end">
        <Box w={3}>
          <Search onChange={(event) => {
              setSearchTerm(event.target.value);
              onPageChange({
                page: 1,
                itemsPerPage
              })
            }} value={searchTerm} placeholder='Enter a domain' />
        </Box>
      </Box>

      <Box mb="md" j="space-between" a="flex-end">
        <Box w="auto">
          <ResourceCount count={backupsNotRestored.length} resource={backupsNotRestored.length === 1 ? "Backup" : "Backups" } />
        </Box>
      </Box>

      {
        paginated && paginated.map(backup =>
          <Box data-qa="websiteBackup" key={backup.websiteId} mb="lg">
            <WebsiteBackupCard
              websiteBackup={{
                filesSize: backup.filesSize,
                finishedAt: backup.createdAt,
                snapshotDirName: "xxx",
                id: backup.snapshotId,
                storageKind: BackupStorageKind.enhance,
                startedAt: backup.createdAt,
                kind: BackupKind.archive,
                mysqlDbsCount: backup.mysqlDbsCount,
                emailsCount: backup.emailsCount,
                mysqlDbsStatus: OperationStatus.successful
              }}
              website={website}
              member={member}
              creatingBackup={false}
              setRestoreState={() => {}}
              setWebsiteBackup={() => {}}
              showEmails={backup.emailsCount > 0}
              vaultInfo={{
                serverId: backup.serverId,
                domain: backup.primaryDomain,
                websiteId: backup.websiteId,
                restoreVaultBackup,
                createdAt: backup.createdAt,
                customer: backup.customer
              }}
              searchTerm={searchTerm}
              handleDeleteBackups={handleDeleteBackups}
            />

          </Box>
        )
      }
    <Pagination itemsPerPage={itemsPerPage} page={page} onPageChange={onPageChange} totalItems={backups.length} />
    </Box>
  );
};

const mapStateToProps = (state: AppState) => ({
  member: sessionSelectors.getMember(state),
});

export const VaultOverview = connect(mapStateToProps, dispatchToProps)(memo(_VaultOverview));
